'use client';

import { CSSProperties, FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicImage } from '@prismicio/react';
import DynamicMarquee from '@/components/DynamicMarquee';
import { StackedHeaderCardsRow as SectionShell } from '@/components/redesign/StackedHeaderCardsRow';

type Props = {
  slice: Content.IntroMediaGridSlice;
};

// Same soft edge fade the HeroVelxEngine redesign uses on its logo ribbon —
// feathers the left/right edges so cards slide in and out of the row without a
// hard cut. Kept inline rather than shared to avoid coupling this variant to
// other slices; the mask is six lines of style, not a component.
const FADE_EDGE_MASK: CSSProperties = {
  WebkitMaskImage:
    'linear-gradient(to right, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)',
  maskImage:
    'linear-gradient(to right, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

/**
 * IntroMediaGrid variation that reuses the redesign section shell
 * (`StackedHeaderCardsRow`) so the theming, bi-tone heading and 1280px
 * bounded container match every other redesign slice on the page. The cards
 * row is the same two-marquee pattern the original `HorizontalCarousel`
 * variation uses (top row scrolls left, bottom row scrolls right), but
 * wrapped in the shared edge-fade mask.
 */
const ThemedContainedCarousel: FC<Props> = ({ slice }) => {
  const primary =
    slice.primary as Content.IntroMediaGridSliceThemedContainedCarouselPrimary;
  const {
    theme,
    title,
    subtitle,
    description,
    media_items: mediaItems,
  } = primary;

  const items = mediaItems ?? [];
  const half = Math.ceil(items.length / 2);
  const topRow = items.slice(0, half);
  const bottomRow = items.slice(half);

  return (
    <SectionShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      theme={theme}
      title={title}
      subtitle={subtitle}
      description={description}
      // The shell adds `[&:not(:first-child)]:mt-4` to every description
      // paragraph so multi-paragraph descriptions get 16px between them; the
      // side effect is a stray 16px above the FIRST paragraph too, because
      // the paragraph is the second DOM child of the flex column (right
      // after the `<h2>`). This variant only ever has a single description
      // paragraph, so we cancel the rule via `cn` merging.
      descriptionParagraphClassName="[&:not(:first-child)]:mt-0"
      renderCardsRow={() => (
        <div className="relative w-full" style={FADE_EDGE_MASK}>
          <div className="flex flex-col gap-4 md:gap-6">
            {topRow.length > 0 && (
              <DynamicMarquee speed={40} gradient={false} direction="left">
                {topRow.map((item, idx) => (
                  <div
                    key={`top-${idx}`}
                    className="mx-2 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-[16px] md:mx-3 md:h-[240px] md:w-[380px]"
                  >
                    <PrismicImage
                      field={item.media}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </DynamicMarquee>
            )}

            {bottomRow.length > 0 && (
              <DynamicMarquee speed={40} gradient={false} direction="right">
                {bottomRow.map((item, idx) => (
                  <div
                    key={`bottom-${idx}`}
                    className="mx-2 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-[16px] md:mx-3 md:h-[240px] md:w-[380px]"
                  >
                    <PrismicImage
                      field={item.media}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </DynamicMarquee>
            )}
          </div>
        </div>
      )}
    >
      {null}
    </SectionShell>
  );
};

export default ThemedContainedCarousel;
